import  { useState, ChangeEvent, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import MainLogo from "../../components/logo";
import { authApi } from "../../lib/api";
import { Loader } from "../../components/ui/loader";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { COUNTRY_DATA } from '../../utils/countryCode';
import { LANGUAGE_OPTIONS } from '../../constants';
import { getCountryFromPath } from '../../utils/countryCode';
import { useDispatch, useSelector } from 'react-redux';
import { setCountry } from '../../store/slices/sessionSlice';
import { setCountryPrefix } from '../../store/countrySlice';

import webbg from "../../assets/webbg.png";
import mobilebg from "../../assets/mobilebg.png";

export const Login = (): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { country } = useParams();
  const [mobileNumber, setMobileNumber] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const initialCountryObj = getCountryFromPath(location.pathname);
  const [countryCode, setCountryCode] = useState(initialCountryObj ? initialCountryObj.code : "+91");
  const [flag, setFlag] = useState(initialCountryObj ? initialCountryObj.flag : COUNTRY_DATA['in'].flag);
  const [error, setError] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const dispatch = useDispatch();
  const sessionCountry = useSelector((state: any) => state.session.country);
  const userIsLoggedIn = useSelector((state: any) => !!state.user.token);
  const [selectedCountry, setSelectedCountry] = useState('in');

  const getInitialLanguage = () => {
    const storedLang = localStorage.getItem('i18nextLng') || 'en';
    const lang = LANGUAGE_OPTIONS.find(l => l.code === storedLang);
    return lang ? lang.display : LANGUAGE_OPTIONS[0].display;
  };

  const [currentLang, setCurrentLang] = useState(getInitialLanguage());

  // Effect to sync language selector with i18n language
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const lang = LANGUAGE_OPTIONS.find(l => l.code === lng);
      setCurrentLang(lang ? lang.display : LANGUAGE_OPTIONS[0].display);
    };
    handleLanguageChange(i18n.language);
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const handleLanguageChange = (value: string) => {
    setCurrentLang(value);
    const lang = LANGUAGE_OPTIONS.find(l => l.display === value);
    const newLang = lang ? lang.code : LANGUAGE_OPTIONS[0].code;
    i18n.changeLanguage(newLang);
    localStorage.setItem('i18nextLng', newLang);
  };

  // Add effect to update error message when language changes
  useEffect(() => {
    if (error) {
      const validation = validateMobileNumber(mobileNumber, countryCode);
      setError(validation.message);
    }
  }, [i18n.language]);

  // Use COUNTRY_OPTIONS for validation and select
  const validateMobileNumber = (number: string, code: string) => {
    const country = Object.values(COUNTRY_DATA).find((c: typeof COUNTRY_DATA[keyof typeof COUNTRY_DATA]) => c.code === code);
    if (!country) {
      return { isValid: false, message: t('invalidCountryCode') };
    }
    if (!country.pattern.test(number)) {
      return { isValid: false, message: t(country.messageKey) };
    }
    return { isValid: true, message: '' };
  };

  const handleMobileNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Strip non-digit characters and then set the value
    const digitsOnly = value.replace(/\D/g, '');
    
    setMobileNumber(digitsOnly);
    // Only validate if we have reached the maximum length
    if (digitsOnly.length === getMobileNumberLength(countryCode)) {
      const validation = validateMobileNumber(digitsOnly, countryCode);
      setError(validation.message);
    } else {
      // Clear error if we're still typing
      setError('');
    }
  };

  const handleMobileNumberBlur = () => {
    // Validate on blur if we have any input
    if (mobileNumber) {
      const validation = validateMobileNumber(mobileNumber, countryCode);
      setError(validation.message);
    }
  };

  const handleCountryCodeChange = (value: string) => {
    setCountryCode(value);
    setMobileNumber("");
    setError("");
  };

  const isMobileValid = () => {
    const validation = validateMobileNumber(mobileNumber, countryCode);
    return validation.isValid;
  };

  const handleContinue = async () => {
    setIsLoading(true);
    setError(""); // Clear any previous errors
    try {
      // Remove the + sign from country code before sending
      const cleanCountryCode = countryCode.replace("+", "");
      const response = await authApi.sendOtp(mobileNumber, cleanCountryCode);
      toast.success(response.message);
      // Extract country from the path
      const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
      const country = countryMatch ? countryMatch[1] : "in";
      dispatch(setCountry(country));
      dispatch(setCountryPrefix(country));
      navigate(`/${country}/otp`, { state: { mobileNumber, countryCode } });
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      let errorMessage = t('errorDuringLogin');
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getMobileNumberLength = (code: string) => {
    const country = Object.values(COUNTRY_DATA).find((c: typeof COUNTRY_DATA[keyof typeof COUNTRY_DATA]) => c.code === code);
    return country ? country.length : 10;
  };

  // Add useEffect to handle location state
  useEffect(() => {
    const state = location.state as { mobileNumber?: string; countryCode?: string } | null;

    
    if (state?.mobileNumber) {
      setMobileNumber(state.mobileNumber);
      if (state.countryCode) {
        setCountryCode(state.countryCode);
      }
      // Validate the mobile number
      const validation = validateMobileNumber(state.mobileNumber, state.countryCode || "+91");
      setError(validation.message);
    }
  }, [location]);

  // Add state persistence
  useEffect(() => {
    if (mobileNumber) {
      sessionStorage.setItem('lastMobileNumber', mobileNumber);
      sessionStorage.setItem('lastCountryCode', countryCode);
    }
  }, [mobileNumber, countryCode]);

  // Check for persisted state on mount
  useEffect(() => {
    const savedMobileNumber = sessionStorage.getItem('lastMobileNumber');
    const savedCountryCode = sessionStorage.getItem('lastCountryCode');
    
    if (savedMobileNumber && !mobileNumber) {
      setMobileNumber(savedMobileNumber);
      if (savedCountryCode) {
        setCountryCode(savedCountryCode);
      }
    }
  }, []);

  // Update country code and flag if URL changes
  useEffect(() => {
    const countryObj = getCountryFromPath(location.pathname);
    if (countryObj) {
      setCountryCode(countryObj.code);
      setFlag(countryObj.flag);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (userIsLoggedIn) {
      if (country && sessionCountry && country.toLowerCase() === sessionCountry.toLowerCase()) {
        navigate(`/${country}/my-predictions`, { replace: true });
      }
    }
  }, [userIsLoggedIn, country, sessionCountry, navigate]);

  useEffect(() => {
    if (!userIsLoggedIn) {
      const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
      const urlCountry = countryMatch ? countryMatch[1] : "in";
      console.log('Setting session country in Login:', urlCountry);
      dispatch(setCountry(urlCountry.toLowerCase()));
    }
  }, [location.pathname, dispatch, userIsLoggedIn]);

  const handleCountrySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountry(e.target.value);
  };

  const handleLogin = () => {
    dispatch(setCountryPrefix(selectedCountry));
    console.log('QA deploy check---');
    navigate(`/${selectedCountry}/login`);
  };

  // Guard: do not render Login if session country and URL country do not match
  if (
    sessionCountry &&
    country &&
    sessionCountry.toLowerCase() !== country.toLowerCase()
  ) {
    return null;
  }

  return (
    <>
      <Loader isLoading={isLoading} message={t('sendingOtp')} />
      <div className="bg-white flex flex-row justify-center w-full h-screen">
        {/* <div className="relative w-full max-w-[1440px] h-full flex"> */}
        <div className="bg-white w-full h-full relative flex">
          {/* Left side - Background image */}
          <div className="hidden sm:block relative flex-grow">
            <img
              className="w-full h-full object-cover"
              alt={t('agriculturalLandscape')}
              src={webbg}
            />
          </div>

          <div 
            className="w-full sm:w-[441px] h-full flex flex-col bg-white"
          >
            <div 
              className="absolute inset-0 bg-no-repeat bg-contain bg-bottom sm:hidden"
              style={{ 
                backgroundImage: `url(${mobilebg})`
              }}
            />
            <div className="p-8 flex flex-col h-full relative">
              {/* Logo */}
              <div className="flex justify-between items-center mb-12">
                <MainLogo/>
              </div>
              {/* Form content */}
              <Card className="border-none shadow-none">
                <CardContent className="p-0">
                  <h1 className="text-2xl font-medium mb-2">{t('welcome')},</h1>
                  <p className="text-neutralneutral-60 mb-8">
                    {t('enterDetailsToContinue')}
                  </p>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label htmlFor="mobile" className="text-sm font-medium">
                        {t('mobileNumber')}<span className="text-red-500">*</span>
                      </label>
                      <div className="flex">
                        <div className="w-28 rounded-r-none border-r-0 flex items-center px-3 py-2 border border-input bg-background h-9">
                          <div className="flex items-center gap-2">
                            <img src={flag} alt={t(`${Object.values(COUNTRY_DATA).find((c: typeof COUNTRY_DATA[keyof typeof COUNTRY_DATA]) => c.code === countryCode)?.name.toLowerCase()}Flag`)} className="w-5 h-3" />
                            {countryCode}
                          </div>
                        </div>
                        <Input
                          id="mobile"
                          className={`rounded-l-none flex-1 h-9 ${error ? 'border-red-500' : ''}`}
                          placeholder={t('mobileNumber')}
                          value={mobileNumber}
                          onChange={handleMobileNumberChange}
                          onBlur={handleMobileNumberBlur}
                          maxLength={getMobileNumberLength(countryCode)}
                          data-testid="mobile-input"
                        />
                      </div>
                      {error && (
                        <p className="text-xs text-red-500 mt-1" data-testid="mobile-error">
                          {error}
                        </p>
                      )}
                    </div>
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="terms"
                        checked={termsAccepted}
                        onCheckedChange={(checked: boolean | 'indeterminate') => setTermsAccepted(checked as boolean)}
                        className="mt-1 data-[state=checked]:bg-greengreen-70 data-[state=checked]:border-greengreen-70"
                      />
                      <label htmlFor="terms" className="text-sm">
                        {t('agreeToTerms')} {" "}
                        <a href="#" className="text-blue-500 hover:underline" onClick={e => { e.preventDefault(); setShowTerms(true); }}>
                          {t('termsAndConditions')}
                        </a>{" "}
                        {t('and')}{" "}
                        <a href="#" className="text-blue-500 hover:underline" onClick={e => { e.preventDefault(); setShowPrivacy(true); }}>
                          {t('privacyPolicy')}
                        </a>
                      </label>
                    </div>
                    <Button
                      className="w-full bg-green-700 hover:bg-green-800 text-white py-6 rounded-md mb-6 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleContinue}
                      disabled={!isMobileValid() || !termsAccepted}
                    >
                      {t('continue')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};